import type { ComponentDocs } from '../component-docs.js';

/**
 * What `<caos-usage-bar>` says about itself.
 *
 * It looks almost exactly like `<caos-progress-bar>` and means the opposite thing, which is the
 * single most important sentence on this page. A progress bar fills toward success; a usage bar
 * fills toward failure. Reaching 100% on one is the happy ending and on the other is an outage.
 *
 * Because of that inversion the colour is COMPUTED, not passed. `warn-at` and `danger-at` decide
 * it from `used` and `maximum`. That is deliberate: a limits screen draws twenty of these at once,
 * and if each caller chose its own colour the screen would disagree with itself about what "nearly
 * out" looks like — the one thing a person scanning it is actually reading. Pass `intent`
 * explicitly only when you genuinely know better than the thresholds.
 *
 * Over-limit is a state it draws rather than an overflow it hides: `used` greater than `maximum`
 * pins the fill at full and keeps the numbers truthful. A bar drawn past its own track communicates
 * nothing, and a counter that silently stops at the cap is worse than one that admits you are over.
 */
const docs: ComponentDocs = {
  aliases: ['quota bar', 'limit bar', 'consumption bar', 'allowance meter', 'capacity bar', 'utilisation bar'],

  answers: [
    'show how much of an API allowance has been used this hour',
    'show storage used against storage available',
    'show seats taken out of seats licensed',
    'warn somebody before they run out of something finite',
    'show that an allowance has been exceeded',
  ],

  placements: ['recordPage', 'homePage', 'appPage', 'utilityBar'],

  whenToUse:
    'When something FINITE is being consumed and running out matters — API calls, storage, seats, ' +
    'records against a cap. The bar is read to answer "how close am I to the ceiling", so it colours ' +
    'itself as the ceiling approaches.',

  insteadUse: [
    {
      name: 'ProgressBar',
      when:
        'the bar reports an OPERATION advancing — an import running, a deploy applying. Full is the ' +
        'goal there, so it must not redden as it fills.',
    },
    {
      name: 'StatTile',
      when: 'the number matters and the proportion does not — a total with no ceiling to be a fraction of.',
    },
  ],

  inputs: [
    {
      name: 'used',
      attribute: 'used',
      type: 'number',
      description: 'How much of the allowance is consumed. Non-numeric or absent reads as 0.',
      default: 0,
    },
    {
      name: 'maximum',
      attribute: 'maximum',
      type: 'number',
      description:
        'The size of the allowance. Absent, zero or negative means there is no ceiling to be a ' +
        'fraction of: the bar draws empty and the value line shows the bare count rather than a ratio.',
    },
    {
      name: 'label',
      attribute: 'label',
      type: 'text',
      description: 'What the allowance is, shown above the track and used as the accessible name.',
    },
    {
      name: 'unit',
      attribute: 'unit',
      type: 'text',
      description: 'A unit appended to the value line, e.g. "GB", "calls". Purely presentational.',
    },
    {
      name: 'warnAt',
      attribute: 'warn-at',
      type: 'number',
      description: 'Percentage consumed at which the fill turns warning.',
      default: 75,
    },
    {
      name: 'dangerAt',
      attribute: 'danger-at',
      type: 'number',
      description: 'Percentage consumed at which the fill turns danger.',
      default: 90,
    },
    {
      name: 'intent',
      attribute: 'intent',
      type: 'text',
      options: ['accent', 'success', 'warning', 'danger'],
      description:
        'Overrides the computed colour. Setting it switches the thresholds OFF for this bar — use it ' +
        'only where the caller genuinely knows something the numbers do not.',
    },
    {
      name: 'hideValue',
      attribute: 'hide-value',
      type: 'boolean',
      description: 'Draws the track without the "used of maximum" line, for dense rows where a shared header carries it.',
      default: false,
    },
  ],

  state: [
    {
      name: 'over-limit',
      type: 'boolean attribute on the host',
      description:
        'Reflected onto the host when `used` exceeds `maximum`, so a row or panel around it can style ' +
        'the whole line without recomputing the comparison.',
    },
  ],

  parts: [
    { part: 'bar', is: 'The outer column holding header and track.' },
    { part: 'header', is: 'The label/value row above the track; hidden when both are empty.' },
    { part: 'label', is: 'The name of the allowance.' },
    { part: 'value', is: 'The "used of maximum" line.' },
    { part: 'track', is: 'The unfilled allowance.' },
    { part: 'fill', is: 'The consumed portion.' },
  ],

  accessibility:
    'Carries `role="meter"` with `aria-valuenow`, `aria-valuemin` and — when there is a ceiling — ' +
    '`aria-valuemax`, plus an `aria-valuetext` reading "3,400 of 5,000 calls" so a screen reader hears ' +
    'the ratio rather than a bare number. `meter` rather than `progressbar` is the same distinction ' +
    'the component exists to make: this is a measurement within a known range, not a task advancing. ' +
    'Colour is never the only signal — the value line states the numbers, and over-limit is announced ' +
    'through `aria-valuetext` rather than by the fill turning red.',

  examples: [
    {
      key: 'ramp',
      label: 'The same bar across the ramp',
      summary:
        'One allowance at four points of its own life. The colour is computed from the proportion, so ' +
        'nothing about these four placements differs except the number — which is the whole argument ' +
        'for the component over four hand-coloured bars.',
      placement: [
        { component: 'caos-usage-bar', inputs: { label: 'API calls', used: 1200, maximum: 10000, unit: 'calls' } },
        { component: 'caos-usage-bar', inputs: { label: 'API calls', used: 7800, maximum: 10000, unit: 'calls' } },
        { component: 'caos-usage-bar', inputs: { label: 'API calls', used: 9400, maximum: 10000, unit: 'calls' } },
        { component: 'caos-usage-bar', inputs: { label: 'API calls', used: 10000, maximum: 10000, unit: 'calls' } },
      ],
      stage: { arrange: 'column', needsWidth: 420 },
    },
    {
      key: 'over-limit',
      label: 'Past the ceiling',
      summary:
        'Spent more than the allowance. The fill pins at the full track — a bar drawn past its own end ' +
        'is a drawing bug, not information — while the numbers stay truthful and say by how much.',
      placement: {
        component: 'caos-usage-bar',
        inputs: { label: 'Streaming events', used: 11400, maximum: 10000, unit: 'events' },
      },
      stage: { arrange: 'column', needsWidth: 420 },
    },
    {
      key: 'no-ceiling',
      label: 'An allowance with no ceiling',
      summary:
        'Usage with nothing to be a proportion of. There is no fill and no meter range, because a bar ' +
        'drawn at some arbitrary width would be inventing a limit that does not exist.',
      placement: {
        component: 'caos-usage-bar',
        inputs: { label: 'Records stored', used: 4182339, unit: 'records' },
      },
      stage: { arrange: 'column', needsWidth: 420 },
    },
    {
      key: 'tightened',
      label: 'Thresholds moved in',
      summary:
        'The same 60% under the default ramp and under a tightened one. Where “approaching” starts is ' +
        'a judgement about the allowance, not a property of the number.',
      placement: [
        { component: 'caos-usage-bar', inputs: { label: 'Default ramp — 75 / 90', used: 60, maximum: 100 } },
        { component: 'caos-usage-bar', inputs: { label: 'Tightened — 40 / 55', used: 60, maximum: 100, warnAt: 40, dangerAt: 55 } },
      ],
      stage: { arrange: 'column', needsWidth: 420 },
    },
  ],
};

export default docs;
